import { useState } from "react";
import axios from "axios";
import { MapPin, Phone, Mail, Clock, Send, Loader2, Instagram } from "lucide-react";
import { toast } from "sonner";
import { SALON_EXTERIOR_2, SALON_EXTERIOR } from "@/data/gallery";
import Reveal from "@/components/Reveal";
import TiltCard from "@/components/TiltCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contactos = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Mensagem enviada! Responderei em breve.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Erro ao enviar mensagem. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contactos-page" className="pb-24">
      <section className="container-luxe py-12 grid lg:grid-cols-12 gap-10 items-end">
        <Reveal className="lg:col-span-6">
          <p className="overline mb-4">— Contactos</p>
          <h1 className="font-serif text-6xl sm:text-7xl lg:text-8xl font-light leading-[0.95] text-ink">
            Fale <span className="italic gold-text">comigo</span>
          </h1>
          <p className="mt-6 text-ink/65 max-w-xl text-lg">
            Para marcações, esclarecimentos ou apenas para dizer olá. Resposta em 24 horas.
          </p>
        </Reveal>
        <Reveal className="lg:col-span-6 perspective-1000">
          <TiltCard maxTilt={5}>
            <div className="relative img-frame-3d aspect-[4/3] overflow-hidden">
              <img
                src={SALON_EXTERIOR_2}
                alt="Montra do estúdio Tamara Alves Hairstylist"
                className="w-full h-full object-cover glow-gold"
                data-testid="salon-exterior-photo"
              />
              <div className="absolute bottom-4 left-4 bg-gold-bright text-ink px-4 py-2 text-xs uppercase tracking-[0.2em] shadow-[0_0_20px_rgba(255,215,0,0.4)]">
                Estúdio · Venda do Pinheiro
              </div>
            </div>
          </TiltCard>
        </Reveal>
      </section>

      <section className="container-luxe grid lg:grid-cols-12 gap-12">
        {/* Info side */}
        <aside className="lg:col-span-5 space-y-8">
          <div className="bg-beige-light p-8 border-l-2 border-gold">
            <div className="space-y-6">
              <div className="flex gap-4">
                <MapPin className="w-5 h-5 text-gold flex-shrink-0 mt-1" strokeWidth={1.5} />
                <div>
                  <p className="overline mb-1">Estúdio</p>
                  <p className="text-ink font-serif text-lg">Rua Casal dos Ninhos, 2D</p>
                  <p className="text-ink/65 text-sm">2665-536 Venda do Pinheiro, Portugal</p>
                </div>
              </div>
              <div className="flex gap-4">
                <Phone className="w-5 h-5 text-gold flex-shrink-0 mt-1" strokeWidth={1.5} />
                <div>
                  <p className="overline mb-1">Telefone / WhatsApp</p>
                  <a href="tel:+351910327990" data-testid="contact-phone" className="text-ink font-serif text-lg hover:text-gold">
                    +351 910 327 990
                  </a>
                </div>
              </div>
              <div className="flex gap-4">
                <Mail className="w-5 h-5 text-gold flex-shrink-0 mt-1" strokeWidth={1.5} />
                <div>
                  <p className="overline mb-1">Email</p>
                  <a href="mailto:info@tamaraalves.pt" data-testid="contact-email" className="text-ink font-serif text-lg hover:text-gold">
                    info@tamaraalves.pt
                  </a>
                </div>
              </div>
              <div className="flex gap-4">
                <Clock className="w-5 h-5 text-gold flex-shrink-0 mt-1" strokeWidth={1.5} />
                <div>
                  <p className="overline mb-1">Horário</p>
                  <p className="text-ink text-sm leading-relaxed">
                    Terça a Sexta · 09:00 — 19:00<br />
                    Sábado · 09:00 — 17:00<br />
                    <span className="text-ink/50">Domingo e Segunda · encerrado</span>
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <Instagram className="w-5 h-5 text-gold flex-shrink-0 mt-1" strokeWidth={1.5} />
                <div>
                  <p className="overline mb-1">Instagram</p>
                  <a
                    href="https://www.instagram.com/tamaraalveshairstylist/"
                    target="_blank"
                    rel="noreferrer"
                    className="text-ink font-serif text-lg hover:text-gold"
                  >
                    @tamaraalveshairstylist
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Map */}
          <div className="aspect-square w-full overflow-hidden border border-gold/20" data-testid="contact-map">
            <iframe
              title="Mapa do estúdio Tamara Alves"
              src="https://www.google.com/maps?q=Rua+Casal+dos+Ninhos+2D,+Venda+do+Pinheiro,+Portugal&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0, filter: "grayscale(20%) contrast(1.05)" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </aside>

        {/* Form */}
        <form onSubmit={submit} className="lg:col-span-7 bg-beige-light p-10 lg:p-14 space-y-8" data-testid="contact-form">
          <div>
            <p className="overline mb-2">— Envie-me uma mensagem</p>
            <h2 className="font-serif text-4xl text-ink">
              Conte-me <span className="italic text-gold">o que precisa</span>.
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-8">
            <div>
              <label className="overline block mb-2">Nome *</label>
              <input
                name="name"
                required
                value={form.name}
                onChange={handleChange}
                data-testid="contact-name-input"
                className="input-luxe"
                placeholder="O seu nome"
              />
            </div>
            <div>
              <label className="overline block mb-2">Email *</label>
              <input
                name="email"
                type="email"
                required
                value={form.email}
                onChange={handleChange}
                data-testid="contact-email-input"
                className="input-luxe"
                placeholder="email@exemplo.pt"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="overline block mb-2">Assunto *</label>
              <input
                name="subject"
                required
                value={form.subject}
                onChange={handleChange}
                data-testid="contact-subject-input"
                className="input-luxe"
                placeholder="Marcação, dúvida, sugestão..."
              />
            </div>
            <div className="sm:col-span-2">
              <label className="overline block mb-2">Mensagem *</label>
              <textarea
                name="message"
                rows={5}
                required
                value={form.message}
                onChange={handleChange}
                data-testid="contact-message-input"
                className="input-luxe resize-none"
                placeholder="Conte-me um pouco mais..."
              />
            </div>
          </div>

          <button type="submit" disabled={loading} data-testid="contact-submit-btn" className="btn-gold disabled:opacity-50">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> A enviar...</> : <>Enviar Mensagem <Send className="w-4 h-4" /></>}
          </button>
        </form>
      </section>
    </div>
  );
};

export default Contactos;
